import ReactFC from "react-fusioncharts";
import FusionCharts from "fusioncharts/core";
import Column2D from "fusioncharts/charts";
import FusionTheme from "fusioncharts/themes/es/fusioncharts.theme.fusion";
import _ from "lodash";
import { indicatorAnalysisOptions } from "utils/constant";
import {
  ensureFullYear,
  formatWeeklyDates,
  monthList,
  numericFormatter,
  separateByYear,
  separateByYearAndEnsureFullYear,
} from "utils/common";
import { formatKelengkapanPeriodLabel, filterRowsInPeriod, toDeathSeries } from "./chartLabels";

ReactFC.fcRoot(FusionCharts, Column2D, FusionTheme);

const cascadeFont = (str) =>
  (str || "").replace(/\b\w+/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });

const Chart = ({
  data,
  totalData,
  type,
  indicator,
  period,
  level,
  levelIdSelected,
}) => {
  const indicatorConfigTren = () => {
    let values = {};

    const chartData = filterRowsInPeriod(data, period);

    const uniqueWeeklyLabels = _.uniqBy(
      Object.values(chartData)?.map((r) => {
        const parts = r.dates?.split("-");
        return {
          label:
            period === "Mingguan"
              ? `${formatWeeklyDates(r.dates)}`
              : period === "Harian"
              ? `${parts?.[1]}-${parts?.[2]}`
              : `${r.dates}`,
          year: r?.year,
        };
      }),
      "label"
    ).map((item) => ({ label: item.label }));

    // Helper: tooltip for data point (Bulanan/Mingguan/Harian)
    const mkTooltip = (r, val) => {
      const parts = r.dates?.split("-");
      if (period === "Mingguan") {
        return `${formatWeeklyDates(r.dates)} ${parts[0]}: ${val}`;
      }
      if (period === "Bulanan") {
        const mi = parseInt(parts[1], 10) - 1;
        return `${monthList[mi]} ${parts[0]}: ${val}`;
      }
      return `${r.dates}: ${val}`; // Harian
    };

    const categories = [
      {
        category:
          period === "Tahunan" || period === "Bulanan"
            ? period === "Tahunan"
              ? monthList.map((r) => ({ label: r }))
              : chartData.map((item) => {
                  const parts = item.dates?.split("-");
                  const monthIndex = parseInt(parts?.[1], 10) - 1;
                  const label = monthList[monthIndex];
                  return { label };
                })
            : uniqueWeeklyLabels,
      },
    ];

    const lineColors = [
      "#1a73e8", "#e8710a", "#0d904f", "#d93025",
      "#9334e6", "#f9ab00", "#12b5cb", "#e91e63",
    ];

    if (indicator === "indikator_3") {
      if (period === "Tahunan") {
        const yd = separateByYear(data);
        const years = Object.keys(yd);
        values = {
          categories,
          dataset: years.map((y, i) => ({
            seriesname: y,
            color: lineColors[i % lineColors.length],
            renderas: "line",
            data: (ensureFullYear(yd[y], y, false) || []).map((r) => ({
              value: r.death,
            })),
          })),
        };
      } else {
        const yd = separateByYear(data);
        const years = Object.keys(yd);
        const catLabels = categories[0].category.map((c) => c.label);
        values = {
          categories,
          dataset: years.map((y, i) => ({
            seriesname: y,
            color: lineColors[i % lineColors.length],
            renderas: "line",
            data: catLabels.map((label) => {
              const match = yd[y].find((r) => {
                const parts = r.dates?.split("-");
                const rLabel =
                  period === "Mingguan"
                    ? `${formatWeeklyDates(r.dates)}`
                    : period === "Bulanan"
                    ? monthList[parseInt(parts[1], 10) - 1]
                    : period === "Harian"
                    ? `${parts[1]}-${parts[2]}`
                    : `${r.dates}`;
                return rLabel === label;
              });
              return {
                value: match ? match.death : null,
                toolText: match
                  ? mkTooltip(match, match.death)
                  : `${label}: -`,
              };
            }),
          })),
        };
      }
    } else if (indicator === "indikator_16") {
      if (period === "Tahunan") {
        const yd = separateByYear(data);
        const years = Object.keys(yd);
        values = {
          categories,
          dataset: years.map((y, i) => ({
            seriesname: y,
            color: lineColors[i % lineColors.length],
            renderas: "line",
            data: (ensureFullYear(yd[y], y, false) || []).map((r) => ({
              value: r.value,
            })),
          })),
        };
      } else if (period === "Bulanan") {
        // Single line across the full month timeline (supports multi-year ranges).
        // Labels include the year (e.g. "Januari 2025") so months from different
        // years are not collapsed/duplicated on the x-axis.
        const sorted = [...chartData].sort(
          (a, b) => new Date(a.dates) - new Date(b.dates)
        );
        values = {
          categories: [
            {
              category: sorted.map((r) => {
                const parts = r.dates?.split("-");
                const monthIndex = parseInt(parts[1], 10) - 1;
                return { label: `${monthList[monthIndex]} ${parts[0]}` };
              }),
            },
          ],
          dataset: [
            {
              seriesname: "SKD Malaria",
              color: lineColors[0],
              renderas: "line",
              data: sorted.map((r) => {
                const parts = r.dates?.split("-");
                const monthIndex = parseInt(parts[1], 10) - 1;
                return {
                  value: r.value,
                  toolText: `${monthList[monthIndex]} ${parts[0]}: ${r.value}`,
                };
              }),
            },
          ],
        };
      } else {
        const yd = separateByYear(data);
        const years = Object.keys(yd);
        const catLabels = categories[0].category.map((c) => c.label);
        values = {
          categories,
          dataset: years.map((y, i) => ({
            seriesname: y,
            color: lineColors[i % lineColors.length],
            renderas: "line",
            data: catLabels.map((label) => {
              const match = yd[y].find((r) => {
                const parts = r.dates?.split("-");
                const rLabel =
                  period === "Mingguan"
                    ? `${formatWeeklyDates(r.dates)}`
                    : period === "Harian"
                    ? `${parts[1]}-${parts[2]}`
                    : `${r.dates}`;
                return rLabel === label;
              });
              return {
                value: match ? match.value : null,
                toolText: match
                  ? mkTooltip(match, match.value)
                  : `${label}: -`,
              };
            }),
          })),
        };
      }
    } else if (indicator === "indikator_20") {
      // Dua seri bertumpuk: kematian dengan malaria, lalu sisa kematian akibat
      // malaria yang belum terhitung, supaya tinggi batang sama dengan total unik.
      const deathRows = (chartData || []).map(toDeathSeries);
      values = {
        categories: [
          {
            category: (chartData || []).map((r) => ({
              label: cascadeFont(r.name || "") || "-",
            })),
          },
        ],
        dataset: [
          {
            seriesname: "Kematian dengan Malaria",
            color: lineColors[0],
            data: deathRows.map((r) => ({ value: r.withMalaria })),
          },
          {
            seriesname: "Kematian akibat Malaria",
            color: lineColors[1],
            data: deathRows.map((r) => ({ value: r.causedOnly })),
          },
        ],
      };
    }
    return values;
  };

  const isTrenIndicator =
    indicator === "indikator_3" ||
    indicator === "indikator_16" ||
    indicator === "indikator_20";

  const percentIndicator =
    indicator === "indikator_1" ||
    indicator === "indikator_4" ||
    indicator === "indikator_5" ||
    indicator === "indikator_6" ||
    indicator === "indikator_10" ||
    indicator === "indikator_11" ||
    indicator === "indikator_14" ||
    indicator === "indikator_15" ||
    indicator === "indikator_19" ||
    indicator === "indikator_25";

  const indicatorName =
    indicatorAnalysisOptions.find((f) => f.value === indicator)?.label || "";

  const chartConfigs = {
    type: type || "column2d",
    width: "100%",
    height: "600",
    dataFormat: "json",
    dataSource: {
      chart: {
        plotToolText:
          indicator === "indikator_1" ||
          indicator === "indikator_4" ||
          indicator === "indikator_5" ||
          indicator === "indikator_9" ||
          indicator === "indikator_10" ||
          indicator === "indikator_14" ||
          indicator === "indikator_24" ||
          indicator === "indikator_6" ||
          indicator === "indikator_8" ||
          indicator === "indikator_11" ||
          indicator === "indikator_15" ||
          indicator === "indikator_19" ||
          indicator === "indikator_25"
            ? `$label, $value $displayValue`
            : indicator === "indikator_17" ||
              indicator === "indikator_18" ||
              indicator === "indikator_21" ||
              indicator === "indikator_22" ||
              indicator === "indikator_23" ||
              indicator === "indikator_23A" ||
              indicator === "indikator_27"
            ? `$label, $dataValue/${numericFormatter(data?.total)}`
            : indicator === "indikator_12"
            ? `$label, $dataValue/${numericFormatter(totalData)}`
            : indicator === "indikator_20"
            ? `$label, $seriesName: $value`
            : "",
        drawAnchors: "1",
        drawCustomLegendIcon: "1",
        legendIconBorderThickness: isTrenIndicator ? "12" : "3",
        exportEnabled: "1",
        exportFileName: `${indicatorName.replace(
          / /g,
          "_"
        )}_${levelIdSelected.replace(/ /g, "_")}_${moment().format(
          "DDMMYYYY"
        )}`,
        caption: "",
        subCaption: "",
        xAxisName: "",
        yAxisMaxValue: percentIndicator ? "100" : null,
        yAxisName: indicatorName || "Kelengkapan %",
        numberSuffix: "",
        theme: "fusion",
        formatNumberScale: "0",
        labelDisplay: "rotate",
        slantLabel: "1",
        showLabels: "1",
        showValues: "1",
        valueFontSize: "12",
        rotateValues: "1",
        placevaluesinside: "0",
        canvasTopPadding: "100",
        numberSuffix: percentIndicator ? "%" : "",
      },
      ...indicatorConfigTren(),
      ...(isTrenIndicator
        ? {}
        : {
            data:
              indicator === "indikator_17" ||
              indicator === "indikator_18" ||
              indicator === "indikator_21" ||
              indicator === "indikator_22" ||
              indicator === "indikator_23" ||
              indicator === "indikator_23A" ||
              indicator === "indikator_27"
                ? Object.keys(data?.case).map((r) => ({
                    name:
                      indicator === "indikator_21" && r === "Tanpa Petugas"
                        ? "NA"
                        : indicator === "indikator_17" && r === "pcd"
                        ? "PCD"
                        : indicator === "indikator_17" && r === "fup"
                        ? "FUP"
                        : indicator === "indikator_17" && r === "mbs"
                        ? "MBS"
                        : indicator === "indikator_17" && r === "surkon"
                        ? "Survei Kontak"
                        : indicator === "indikator_17" && r === "surkonpe"
                        ? "Survei Non PE"
                        : indicator === "indikator_17" && r === "kunrum"
                        ? "Kunjungan Rumah"
                        : indicator === "indikator_17" && r === "surmig"
                        ? "Surveilans Migras"
                        : indicator === "indikator_17" && r === "pusling"
                        ? "Pusling"
                        : indicator === "indikator_17" && r === "posyandu"
                        ? "Posyandu"
                        : indicator === "indikator_17" && r === "bumil"
                        ? "Skrining Bumil"
                        : indicator === "indikator_17" && r === "mtbs"
                        ? "Skrining Balita Sakit/MTBS"
                        : indicator === "indikator_17" && r === "afs"
                        ? "Active Fever Screening (AFS)"
                        : indicator === "indikator_17" && r === "ckg"
                        ? "Cek Kesehatan Gratis (CKG)"
                        : indicator === "indikator_17" && r === "edat"
                        ? "EDAT"
                        : r,
                    value: data?.case[r] || "0",
                  }))
                : (data || []).map((r) => {
                    let name = "";
                    let value = "";
                    if (indicator === "indikator_1") {
                      name = cascadeFont(
                        formatKelengkapanPeriodLabel(r.name, level)
                      );
                      value = `${r.percent}%, ${numericFormatter(
                        r.num
                      )}/${numericFormatter(r.denum)}`;
                    } else if (
                      indicator === "indikator_2" ||
                      indicator === "indikator_30"
                    ) {
                      name = cascadeFont(r.name);
                      value = r.positive_case;
                    } else if (indicator === "indikator_4") {
                      name = cascadeFont(r.name);
                      value = `${r.suspect_confirm}%, ${numericFormatter(
                        r.num
                      )}/${numericFormatter(r.denum)}`;
                    } else if (indicator === "indikator_5") {
                      name = cascadeFont(r.name);
                      value = `${r.percent}%, ${numericFormatter(
                        r.num
                      )}/${numericFormatter(r.denum)}`;
                    } else if (indicator === "indikator_6") {
                      name = r.name;
                      value = `${r.value}%, ${numericFormatter(
                        r.num
                      )}/${numericFormatter(r.denum)}`;
                    } else if (indicator === "indikator_7") {
                      name = cascadeFont(r.name);
                      value = r.value;
                    } else if (indicator === "indikator_8") {
                      name = cascadeFont(r.name);
                      value = `${r.percent}%, ${numericFormatter(
                        r.num
                      )}/${numericFormatter(r.denum)}`;
                    } else if (indicator === "indikator_9") {
                      name = r.name;
                      value = `${r.percent}%, ${numericFormatter(
                        r.num
                      )}/${numericFormatter(r.denum)}`;
                    } else if (indicator === "indikator_10") {
                      name = r.name;
                      value = `${r.value1}%, ${numericFormatter(
                        r.num
                      )}/${numericFormatter(r.denum)}`;
                    } else if (indicator === "indikator_11") {
                      name = cascadeFont(r.name);
                      value = `${r.standard_medication}%, ${numericFormatter(
                        r.num
                      )}/${numericFormatter(r.denum)}`;
                    } else if (indicator === "indikator_12") {
                      name = cascadeFont(r.parasyte);
                      value = `${r.total_kasus}`;
                    } else if (indicator === "indikator_13") {
                      name = cascadeFont(r.name);
                      value = r.indigenous;
                    } else if (indicator === "indikator_14") {
                      name = r.name;
                      value = `${r.logistic_completeness}%, ${numericFormatter(
                        r.num
                      )}/${numericFormatter(r.denum)}`;
                    } else if (indicator === "indikator_15") {
                      name = cascadeFont(r.name);
                      value = `${r.value || r.percent}%, ${numericFormatter(
                        r.num
                      )}/${numericFormatter(r.denum)}`;
                    } else if (indicator === "indikator_19") {
                      name = cascadeFont(r.name || "");
                      value = `${r.completeness}%, ${numericFormatter(
                        r.num
                      )}/${numericFormatter(r.denum)}`;
                    } else if (indicator === "indikator_24") {
                      name = cascadeFont(r.name || "");
                      value = `${r.api}, ${numericFormatter(
                        r.num
                      )}/${numericFormatter(r.denum)}`;
                    } else if (indicator === "indikator_25") {
                      name = r.name;
                      value = `${r.percent || r.value}%, ${numericFormatter(
                        r.num
                      )}/${numericFormatter(r.denum)}`;
                    }
                    return {
                      label: name,
                      value: value,
                      showLabel: "1",
                    };
                  }) || [],
          }),
    },
  };
  return (
    // eslint-disable-next-line react/react-in-jsx-scope
    <ReactFC {...chartConfigs} />
  );
};

export default Chart;
